// The chat element's six verbs as the console speaks them on the person's
// own connection (impire-hq 02-DESIGN/14-solutions/03-the-console.md § the
// elements, *chat*; 02-DESIGN/19-chat/01-the-chat-solution.md § the verbs;
// hq decision 0083): the models verb; the threads, thread and clear verbs,
// which answer for the person's own threads alone; the transcript verb as a
// stream of events the thread folds into the reply being written — the first
// frame naming the thread and how many older turns were left out — and the
// cancel verb.

import { frame } from "@impire-io/sdk";

import { Bus, CODE_NO_ANSWER, isRefusal } from "../../../platform/bus.ts";
import { verbSubject } from "../../../wire/solutions.ts";
import { escalationOf, headerOf, serviceErrorOf } from "../../approvals/card.ts";
import { CODE_CANCELLED, type Event } from "./reply.ts";
import {
  HEADER_CHAT_THREAD,
  leftOutOf,
  readThread,
  readThreads,
  type ClearReply,
  type DeleteBody,
  type Model,
  type ModelsReply,
  type RenameBody,
  type SendBody,
  type Summary,
  type Thread,
} from "./wire.ts";

/** SEND_IDLE is how long, in milliseconds, a reply may go without a frame: a model thinking sends progress frames well within it. */
export const SEND_IDLE = 120_000;

const decoder = new TextDecoder();

/** Verbs are the six the element declares, all of one role. */
export interface Verbs {
  send: string;
  cancel: string;
  models: string;
  threads: string;
  thread: string;
  clear: string;
}

/**
 * codeOf reads a failure's code and words: a refusal's own — the bus's, or a
 * service's the frame reader threw on an error terminator — an idle stream's
 * *no-answer*, anything else as *failed*.
 */
export function codeOf(err: unknown): { code: string; message: string } {
  if (isRefusal(err)) {
    return { code: err.code, message: err.message };
  }
  const se = serviceErrorOf(err);
  if (se !== undefined) {
    return { code: se.code, message: se.message === "" ? se.code : se.message };
  }
  if (err instanceof frame.IdleError) {
    return { code: CODE_NO_ANSWER, message: err.message };
  }
  return { code: "failed", message: err instanceof Error ? err.message : String(err) };
}

/** Failed is the error event a failure ends the stream with. */
export type Failed = Extract<Event, { kind: "error" }>;

/** failureOf is the error event a failure ends the stream with: its code, and the crossing's id and the thread where the refusal carried them. */
export function failureOf(err: unknown): Failed {
  const e: Failed = { kind: "error", ...codeOf(err) };
  const escalation = escalationOf(err);
  if (escalation !== undefined) {
    e.escalation = escalation;
  }
  const thread = headerOf(err, HEADER_CHAT_THREAD);
  if (thread !== "") {
    e.thread = thread;
  }
  return e;
}

/** Relay is the element's workload, by its service name and id, and the verbs it was declared with. */
export class Relay {
  constructor(
    private readonly bus: Bus,
    private readonly service: string,
    readonly workload: string,
    private readonly verbs: Verbs,
  ) {}

  /** models are the chat-capable instances the person may talk to. */
  async models(): Promise<Model[]> {
    const r = await this.bus.call<ModelsReply>(this.service, verbSubject(this.workload, this.verbs.models));
    return Array.isArray(r.models) ? r.models : [];
  }

  /** threads are the person's threads, newest first. */
  async threads(): Promise<Summary[]> {
    return readThreads(
      await this.bus.call<unknown>(this.service, verbSubject(this.workload, this.verbs.threads)),
    );
  }

  /** thread is one of the person's threads with its turns in order. */
  async thread(id: string): Promise<Thread> {
    const t = readThread(
      await this.bus.call<unknown>(this.service, verbSubject(this.workload, this.verbs.thread, id)),
    );
    if (t === undefined) {
      throw new Error("the thread verb answered no thread");
    }
    return t;
  }

  /** rename gives a thread a title. */
  async rename(id: string, title: string): Promise<void> {
    await this.bus.call<unknown>(this.service, verbSubject(this.workload, this.verbs.thread, id), {
      title,
    } satisfies RenameBody);
  }

  /** remove deletes one thread. */
  async remove(id: string): Promise<void> {
    await this.bus.call<unknown>(this.service, verbSubject(this.workload, this.verbs.thread, id), {
      delete: true,
    } satisfies DeleteBody);
  }

  /** clear deletes every thread of the person's, and says how many. */
  async clear(): Promise<number> {
    const r = await this.bus.call<ClearReply>(this.service, verbSubject(this.workload, this.verbs.clear));
    return typeof r.deleted === "number" ? r.deleted : 0;
  }

  /**
   * send relays one turn — or, with no text, the same transcript again — and
   * yields what comes back as it comes: the request id first, the thread the
   * first frame names, text, progress while the model thinks, then done with
   * the terminator's headers — or error with the refusal's code and, on an
   * escalation, the crossing's id, which ends it. Aborting the signal ends
   * the stream; what was read stands.
   */
  async *send(body: SendBody, signal: AbortSignal): AsyncGenerator<Event, void, undefined> {
    let r: frame.Reader;
    try {
      r = await this.bus.open(this.service, verbSubject(this.workload, this.verbs.send), body, {
        idle: SEND_IDLE,
        signal,
      });
    } catch (err) {
      yield failureOf(err);
      return;
    }
    yield { kind: "request", request: r.request };
    let first = true;
    try {
      for await (const f of r.frames()) {
        if (first) {
          // The reader yields the first frame, whatever its kind, with its headers: the thread rides it.
          first = false;
          yield { kind: "thread", thread: f.headers.get(HEADER_CHAT_THREAD), left_out: leftOutOf(f.headers) };
        }
        switch (f.kind) {
          case frame.Kind.content:
            yield { kind: "text", text: decoder.decode(f.data) };
            break;
          case frame.Kind.progress:
            yield { kind: "progress" };
            break;
          case frame.Kind.terminator:
            break;
        }
      }
      yield { kind: "done", headers: headersOf(r.terminator()) };
    } catch (err) {
      if (signal.aborted) {
        // The person stopped it, or the panel is leaving: cancel said so already.
        yield { kind: "error", code: CODE_CANCELLED, message: CODE_CANCELLED };
        return;
      }
      const c = codeOf(err);
      if (this.bus.current().kind !== "connected" && c.code === CODE_NO_ANSWER) {
        yield { kind: "lost" };
        return;
      }
      yield failureOf(err);
    }
  }

  /** cancel stops the reply the request id names; what arrived stands. */
  async cancel(request: string): Promise<void> {
    if (request === "") {
      return;
    }
    await this.bus.call<unknown>(this.service, verbSubject(this.workload, this.verbs.cancel, request));
  }
}

/** headersOf is the terminator's headers as the outcome reader wants them: a get that answers the empty string where absent. */
function headersOf(h: { get(key: string): string } | undefined): { get(key: string): string } | undefined {
  if (h === undefined) {
    return undefined;
  }
  return { get: (k) => h.get(k) };
}
